import { LitElement, css, html } from "lit";
import { customElement, property } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";

import { styles as spotlightStyles } from "../../utils/spotlight";

import type { TemplateResult } from "lit";
import type { SlideState, WordDefinitionData } from "../../types.d";
import type { SlideViewMode } from "../slide/slide";

@customElement("bp-word-definition-slide")
export class WordDefinitonSlide extends LitElement {
  static styles = [
    css`
      .word-definition-content {
        width: calc(var(--slide-default-width) - var(--size-10));
        display: flex;
        flex-direction: column;
        justify-content: center;
        gap: var(--size-1);
      }
      .language-name {
        background-color: rgba(255, 255, 255, 0.75);
        font-size: var(--font-size-2xl);
        font-weight: var(--font-weight-bold);
        letter-spacing: var(--letter-spacing-wider);
        padding: var(--size-1) var(--size-2);
      }
      .word-definition-card {
        --shadow-fade: var(--fade-amount, 1);
        --shadow-opacity: calc(0.5 - var(--shadow-fade) * 0.5);
        background-color: var(--color-white);
        color: var(--color-black);
        box-shadow: 8px 8px 0px rgba(255, 255, 255, var(--shadow-opacity));
        padding: var(--size-3);
        margin-bottom: var(--size-1);
        font-size: var(--font-size-5xl);
        transition:
          color var(--spotlight-transition-duration),
          background var(--spotlight-transition-duration),
          box-shadow var(--spotlight-transition-duration);
      }
      .word-definition-title {
        font-weight: var(--font-weight-semibold);
      }
      .word-definition-original {
        font-weight: var(--font-weight-normal);
      }
      .word-notes ul {
        margin: var(--size-1) 0 0;
      }
    `,
    spotlightStyles,
  ];

  @property({ attribute: "view-mode" })
  viewMode?: SlideViewMode = "default";

  @property()
  slideState?: SlideState;

  @property()
  wordDefinitionData?: Array<WordDefinitionData>;

  getWordDefinitionContent = (): TemplateResult => {
    let currentLanguage = "";
    const content = [];
    const cardIds = new Set<string>();
    for (const definition of this.wordDefinitionData ?? []) {
      if (definition.language != currentLanguage) {
        content.push(
          html`<div class="language">
            <span class="language-name"
              >${definition.language.toUpperCase()}</span
            >
          </div>`,
        );
        currentLanguage = definition.language;
      }
      const baseCardId = `card-${
        definition.transliteration ?? definition.definition
      }`;
      let cardId = baseCardId;
      let cardIdDuplicateCount = 1;
      while (cardIds.has(cardId)) {
        cardIdDuplicateCount++;
        cardId = baseCardId + "-" + cardIdDuplicateCount;
      }
      cardIds.add(cardId);
      const definitionEquals =
        definition.definition &&
        (definition.transliteration || definition.original)
          ? html` = `
          : null;
      const definitionTransliteration = definition.transliteration?.length
        ? html`<em>${definition.transliteration}</em>`
        : null;
      const definitionOriginalInner =
        definition.language == "hebrew" || definition.language == "aramaic"
          ? html`<bp-text-hebrew>${definition.original}</bp-text-hebrew>`
          : definition.language == "greek"
          ? html`<bp-text-greek>${definition.original}</bp-text-greek>`
          : html``;
      const definitionOriginal =
        definition.original && definition.language !== "latin"
          ? definition.transliteration
            ? html` (<span class="word-definition-original"
                  >${definitionOriginalInner}</span
                >)`
            : html`<span class="word-definition-original"
                >${definitionOriginalInner}</span
              >`
          : null;
      const definitionTitle = html`${[
        definition.definition,
        definitionEquals,
        definitionTransliteration,
        definitionOriginal,
      ].filter((part) => part !== null)}`;
      content.push(
        html`<div data-id="${cardId}" class="word-definition-card">
          <div class="word-definition-title">${definitionTitle}</div>
          ${definition.notes?.length
            ? definition.notes.length > 1
              ? html`<div class="word-notes">
                  <ul>
                    ${definition.notes.map(
                      (note) => html`<li>${unsafeHTML(note)}</li>`,
                    )}
                  </ul>
                </div>`
              : html`<div class="word-notes">
                  ${unsafeHTML(definition.notes[0])}
                </div>`
            : ""}
        </div>`,
      );
    }
    return html`${content}`;
  };

  render() {
    return html`
      <bp-zoomable-slide
        view-mode="${this.viewMode}"
        background-color="class-secondary"
        hide-shadow="true"
        hide-title="true"
        .slideState=${{
          ...this.slideState,
          transformState: {
            ...this.slideState?.transformState,
            zoom: 1,
          },
        }}
      >
        <div slot="content">
          <div class="word-definition-content">
            ${this.getWordDefinitionContent()}
          </div>
        </div>
      </bp-scrollable-slide>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bp-word-definition-slide": WordDefinitonSlide;
  }
}
